import { MarkdownFormatterService } from './markdown-formatter.service';

export interface IRenderedText {
  content: string;
  htmlContent: string;
}

/** O texto pertence à mensagem, não à ordem de chegada: dois runs simultâneos
 * não podem compartilhar o mesmo acumulador. */
export const RENDER_INTERVAL_MS = 100;
/**
 * Piso em caracteres, para a rajada que chega toda no mesmo milissegundo.
 * Acima de ~200 a cadência de tempo volta a mandar; abaixo disso o piso dispara
 * a cada delta (o delta médio tem 16 caracteres) e anula o ganho.
 */
export const RENDER_CHUNK = 400;
/** Trecho inicial pintado token a token, para a resposta começar sem espera. */
export const PRIMEIROS_CARACTERES = 200;

export class ResponseBuffer {
  private text = '';
  private html = '';
  private renderedAt = 0;
  private renderedLength = 0;
  private pendente = false;

  constructor(
    readonly messageId: string,
    private readonly formatter: MarkdownFormatterService
  ) {}

  /**
   * Deltas são fatias arbitrárias: `['O','lá']` é `Olá`, sem separador.
   *
   * O markdown é reprocessado por inteiro a cada chamada, então converter todo
   * delta custa O(n²) sobre a resposta — uma de 60 KB chega a 178 MB de HTML
   * descartado. O texto acompanha cada delta; o HTML só é refeito em cadência.
   */
  append(delta: string, now = Date.now()): IRenderedText {
    this.text += delta;
    this.pendente = true;
    if (this.devePintar(now)) {
      this.renderNow(now);
    }
    return { content: this.text, htmlContent: this.html };
  }

  /** O texto final do servidor substitui o acumulado; nunca é concatenado. */
  replace(text: string, now = Date.now()): IRenderedText {
    this.text = text;
    this.renderNow(now);
    return { content: this.text, htmlContent: this.html };
  }

  /** Fecha a mensagem com o acumulado inteiro, sem esperar a cadência. */
  flush(now = Date.now()): IRenderedText {
    this.renderNow(now);
    return { content: this.text, htmlContent: this.html };
  }

  private devePintar(now: number): boolean {
    // O começo da resposta aparece na hora: esperar a cadência aqui faria a
    // conversa demorar a reagir logo depois do envio.
    if (this.text.length <= PRIMEIROS_CARACTERES) {
      return true;
    }
    // Uma rajada inteira pode cair no mesmo milissegundo, e aí só o tempo não
    // libera nada — o piso de caracteres cobre esse caso.
    return (
      now - this.renderedAt >= RENDER_INTERVAL_MS ||
      this.text.length - this.renderedLength >= RENDER_CHUNK
    );
  }

  private renderNow(now: number): void {
    this.renderedAt = now;
    this.renderedLength = this.text.length;
    this.pendente = false;
    this.html = this.format();
  }

  /** Há texto recebido que a cadência ainda não converteu. */
  get hasPendingRender(): boolean {
    return this.pendente;
  }

  get isEmpty(): boolean {
    return this.text.length === 0;
  }

  private format(): string {
    return this.text ? this.formatter.format(this.text) : '';
  }
}
